import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/config';
import DashboardHeader from '../common/DashboardHeader';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  WrenchScrewdriverIcon,
  UsersIcon,
  CalendarIcon,
  ClockIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentTextIcon,
  UserGroupIcon,
  CubeIcon,
  BellIcon,
  CreditCardIcon,
  ChevronRightIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  ChartPieIcon,
  CalculatorIcon,
  CogIcon,
  StarIcon,
  FireIcon,
  LightBulbIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Line, Doughnut, Bar, Radar, Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface DashboardStats {
  todayRevenue: number;
  monthlyRevenue: number;
  yearlyRevenue: number;
  activeJobs: number;
  totalCustomers: number;
  pendingInvoices: number;
  totalEmployees: number;
  lowStockItems: number;
  todayAppointments: number;
  completedJobsToday: number;
  averageServiceTime: number;
  customerSatisfaction: number;
  recurringCustomers: number;
  newCustomersThisMonth: number;
  averageJobValue: number;
  collectionRate: number;
  inventoryValue: number;
  profitMargin: number;
  employeeProductivity: number;
}

interface RevenueAnalysis {
  daily: number[];
  weekly: number[];
  monthly: number[];
  services: { name: string; revenue: number }[];
  topCustomers: { name: string; spent: number }[];
  paymentMethods: { method: string; amount: number }[];
}

interface ServiceMetrics {
  popularServices: { name: string; count: number; revenue: number; avgTime: number }[];
  serviceCategories: { category: string; percentage: number }[];
  seasonalTrends: { month: string; services: number }[];
  completionRates: { service: string; rate: number }[];
}

interface CustomerInsights {
  segments: { type: string; count: number; value: number }[];
  retentionRate: number;
  lifetimeValue: number;
  churnRate: number;
  satisfactionTrend: number[];
  feedbackSummary: { positive: number; neutral: number; negative: number };
}

interface FinancialHealth {
  cashFlow: { month: string; inflow: number; outflow: number }[];
  profitability: { gross: number; net: number; operating: number };
  expenses: { category: string; amount: number }[];
  budgetVsActual: { category: string; budget: number; actual: number }[];
}

const EnhancedGarageOwnerDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [revenueAnalysis, setRevenueAnalysis] = useState<RevenueAnalysis | null>(null);
  const [serviceMetrics, setServiceMetrics] = useState<ServiceMetrics | null>(null);
  const [customerInsights, setCustomerInsights] = useState<CustomerInsights | null>(null);
  const [financialHealth, setFinancialHealth] = useState<FinancialHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedView, setSelectedView] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // In production, these would be actual API calls
      // For now, setting mock data
      setMockData();
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setMockData = () => {
    setStats({
      todayRevenue: 2845.750,
      monthlyRevenue: 45250.500,
      yearlyRevenue: 485750.250,
      activeJobs: 18,
      totalCustomers: 342,
      pendingInvoices: 12,
      totalEmployees: 8,
      lowStockItems: 5,
      todayAppointments: 7,
      completedJobsToday: 12,
      averageServiceTime: 2.5,
      customerSatisfaction: 4.7,
      recurringCustomers: 287,
      newCustomersThisMonth: 23,
      averageJobValue: 125.750,
      collectionRate: 94.5,
      inventoryValue: 18500.000,
      profitMargin: 32.5,
      employeeProductivity: 87.3
    });

    setRevenueAnalysis({
      daily: [1850, 2200, 1950, 2450, 2100, 2900, 2845],
      weekly: [12500, 13200, 14100, 15000, 14500],
      monthly: [42000, 44500, 45250, 46000, 47500, 48200],
      services: [
        { name: 'Oil Change', revenue: 8500 },
        { name: 'Brake Service', revenue: 12000 },
        { name: 'Engine Repair', revenue: 15000 },
        { name: 'AC Service', revenue: 6500 },
        { name: 'Tire Service', revenue: 3250 }
      ],
      topCustomers: [
        { name: 'Ahmed Al-Rashid', spent: 3500 },
        { name: 'Fatima Hassan', spent: 2800 },
        { name: 'Mohammed Ali', spent: 2200 },
        { name: 'Sara Ibrahim', spent: 1900 },
        { name: 'Khalid Omar', spent: 1700 }
      ],
      paymentMethods: [
        { method: 'Cash', amount: 18500 },
        { method: 'K-Net', amount: 15000 },
        { method: 'Credit Card', amount: 8500 },
        { method: 'Bank Transfer', amount: 3250 }
      ]
    });

    setServiceMetrics({
      popularServices: [
        { name: 'Oil Change', count: 145, revenue: 8500, avgTime: 0.75 },
        { name: 'Brake Service', count: 82, revenue: 12000, avgTime: 2.5 },
        { name: 'Engine Diagnostics', count: 68, revenue: 6800, avgTime: 1.5 },
        { name: 'AC Service', count: 55, revenue: 6500, avgTime: 1.75 },
        { name: 'Suspension Repair', count: 42, revenue: 8400, avgTime: 3.5 }
      ],
      serviceCategories: [
        { category: 'Maintenance', percentage: 45 },
        { category: 'Repairs', percentage: 30 },
        { category: 'Diagnostics', percentage: 15 },
        { category: 'Upgrades', percentage: 10 }
      ],
      seasonalTrends: [
        { month: 'Jan', services: 280 },
        { month: 'Feb', services: 295 },
        { month: 'Mar', services: 310 },
        { month: 'Apr', services: 350 },
        { month: 'May', services: 380 },
        { month: 'Jun', services: 420 }
      ],
      completionRates: [
        { service: 'Oil Change', rate: 98 },
        { service: 'Brake Service', rate: 95 },
        { service: 'Engine Repair', rate: 92 },
        { service: 'AC Service', rate: 96 },
        { service: 'General Maintenance', rate: 97 }
      ]
    });

    setCustomerInsights({
      segments: [
        { type: 'VIP', count: 45, value: 125000 },
        { type: 'Regular', count: 180, value: 250000 },
        { type: 'Occasional', count: 87, value: 65000 },
        { type: 'New', count: 30, value: 22500 }
      ],
      retentionRate: 84.5,
      lifetimeValue: 1850.500,
      churnRate: 5.2,
      satisfactionTrend: [4.5, 4.6, 4.6, 4.7, 4.7, 4.8],
      feedbackSummary: { positive: 285, neutral: 42, negative: 15 }
    });

    setFinancialHealth({
      cashFlow: [
        { month: 'Jan', inflow: 42000, outflow: 28000 },
        { month: 'Feb', inflow: 44500, outflow: 29500 },
        { month: 'Mar', inflow: 45250, outflow: 30000 },
        { month: 'Apr', inflow: 46000, outflow: 30500 },
        { month: 'May', inflow: 47500, outflow: 31000 },
        { month: 'Jun', inflow: 48200, outflow: 31500 }
      ],
      profitability: { gross: 45.2, net: 32.5, operating: 38.7 },
      expenses: [
        { category: 'Salaries', amount: 12000 },
        { category: 'Inventory', amount: 8500 },
        { category: 'Utilities', amount: 2200 },
        { category: 'Rent', amount: 4500 },
        { category: 'Other', amount: 3300 }
      ],
      budgetVsActual: [
        { category: 'Revenue', budget: 45000, actual: 45250 },
        { category: 'Expenses', budget: 31000, actual: 30500 },
        { category: 'Marketing', budget: 2000, actual: 1800 },
        { category: 'Maintenance', budget: 1500, actual: 1650 }
      ]
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KW', {
      style: 'currency',
      currency: 'KWD',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount);
  };

  const renderMetricCard = (
    title: string,
    value: string | number,
    change?: { value: number; isPositive: boolean },
    icon?: React.ReactNode,
    color: string = 'primary'
  ) => {
    const colorMap: { [key: string]: string } = {
      primary: 'border-[#008B8B] bg-[#008B8B]/10',
      secondary: 'border-[#FF8C00] bg-[#FF8C00]/10',
      success: 'border-[#10B981] bg-[#10B981]/10',
      warning: 'border-[#F59E0B] bg-[#F59E0B]/10',
      danger: 'border-[#EF4444] bg-[#EF4444]/10',
      purple: 'border-[#8B5CF6] bg-[#8B5CF6]/10'
    };

    return (
      <div className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${colorMap[color]}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
            {change && (
              <div className="mt-2 flex items-center">
                {change.isPositive ? (
                  <ArrowUpIcon className="w-4 h-4 text-green-500 mr-1" />
                ) : (
                  <ArrowDownIcon className="w-4 h-4 text-red-500 mr-1" />
                )}
                <span
                  className={`text-sm font-medium ${
                    change.isPositive ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {Math.abs(change.value)}%
                </span>
                <span className="text-sm text-gray-500 ml-1">vs last period</span>
              </div>
            )}
          </div>
          {icon && <div className="ml-4">{icon}</div>}
        </div>
      </div>
    );
  };

  const renderRevenueOverview = () => {
    if (!revenueAnalysis) return null;

    const data = {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      datasets: [
        {
          label: 'Revenue',
          data: revenueAnalysis.daily,
          borderColor: 'rgb(0, 139, 139)',
          backgroundColor: 'rgba(0, 139, 139, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Target',
          data: [2000, 2000, 2000, 2000, 2000, 2500, 2500],
          borderColor: 'rgb(128, 128, 128)',
          borderDash: [5, 5],
          fill: false,
        }
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: 'Weekly Revenue Performance',
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function(value: any) {
              return formatCurrency(value);
            },
          },
        },
      },
    };

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="h-80">
          <Line data={data} options={options} />
        </div>
      </div>
    );
  };

  const renderServicePerformance = () => {
    if (!serviceMetrics) return null;

    const data = {
      labels: serviceMetrics.popularServices.map(s => s.name),
      datasets: [
        {
          label: 'Service Count',
          data: serviceMetrics.popularServices.map(s => s.count),
          backgroundColor: 'rgba(0, 139, 139, 0.8)',
          yAxisID: 'y',
        },
        {
          label: 'Revenue (KWD)',
          data: serviceMetrics.popularServices.map(s => s.revenue),
          backgroundColor: 'rgba(255, 140, 0, 0.8)',
          yAxisID: 'y1',
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: 'Service Performance Analysis',
        },
      },
      scales: {
        y: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          title: {
            display: true,
            text: 'Service Count'
          }
        },
        y1: {
          type: 'linear' as const,
          display: true,
          position: 'right' as const,
          title: {
            display: true,
            text: 'Revenue (KWD)'
          },
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            callback: function(value: any) {
              return formatCurrency(value);
            }
          }
        },
      },
    };

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="h-80">
          <Bar data={data} options={options as any} />
        </div>
      </div>
    );
  };

  const renderCustomerSegmentation = () => {
    if (!customerInsights) return null;

    const data = {
      labels: customerInsights.segments.map(s => s.type),
      datasets: [{
        data: customerInsights.segments.map(s => s.count),
        backgroundColor: [
          'rgba(0, 139, 139, 0.8)',
          'rgba(255, 140, 0, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        title: {
          display: true,
          text: 'Customer Segmentation'
        },
        tooltip: {
          callbacks: {
            label: function(context: any) {
              const segment = customerInsights.segments[context.dataIndex];
              return [
                `${segment.type}: ${segment.count} customers`,
                `Revenue: ${formatCurrency(segment.value)}`
              ];
            }
          }
        }
      }
    };

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="h-80">
          <Doughnut data={data} options={options} />
        </div>
      </div>
    );
  };

  const renderEmployeePerformanceRadar = () => {
    const data = {
      labels: ['Productivity', 'Quality', 'Speed', 'Customer Rating', 'Revenue'],
      datasets: [
        {
          label: 'Ahmed',
          data: [85, 90, 80, 95, 88],
          borderColor: 'rgb(0, 139, 139)',
          backgroundColor: 'rgba(0, 139, 139, 0.2)',
        },
        {
          label: 'Hassan',
          data: [80, 85, 90, 88, 82],
          borderColor: 'rgb(255, 140, 0)',
          backgroundColor: 'rgba(255, 140, 0, 0.2)',
        },
        {
          label: 'Team Average',
          data: [82, 87, 85, 91, 85],
          borderColor: 'rgb(128, 128, 128)',
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          borderDash: [5, 5]
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          beginAtZero: true,
          max: 100
        }
      },
      plugins: {
        title: {
          display: true,
          text: 'Employee Performance Metrics'
        }
      }
    };

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="h-80">
          <Radar data={data} options={options} />
        </div>
      </div>
    );
  };

  const renderFinancialHealth = () => {
    if (!financialHealth) return null;

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold mb-6">Financial Health Score</h3>
        
        {/* Profitability Gauges */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="text-center">
            <div className="relative inline-flex items-center justify-center w-24 h-24">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle cx="48" cy="48" r="36" stroke="currentColor" strokeWidth="8" fill="none" className="text-gray-200"/>
                <circle cx="48" cy="48" r="36" stroke="currentColor" strokeWidth="8" fill="none" 
                  strokeDasharray={`${2 * Math.PI * 36}`}
                  strokeDashoffset={`${2 * Math.PI * 36 * (1 - financialHealth.profitability.gross / 100)}`}
                  className="text-green-600 transition-all duration-300"/>
              </svg>
              <span className="absolute text-xl font-bold">{financialHealth.profitability.gross}%</span>
            </div>
            <p className="mt-2 text-sm font-medium">Gross Margin</p>
          </div>

          <div className="text-center">
            <div className="relative inline-flex items-center justify-center w-24 h-24">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle cx="48" cy="48" r="36" stroke="currentColor" strokeWidth="8" fill="none" className="text-gray-200"/>
                <circle cx="48" cy="48" r="36" stroke="currentColor" strokeWidth="8" fill="none" 
                  strokeDasharray={`${2 * Math.PI * 36}`}
                  strokeDashoffset={`${2 * Math.PI * 36 * (1 - financialHealth.profitability.net / 100)}`}
                  className="text-blue-600 transition-all duration-300"/>
              </svg>
              <span className="absolute text-xl font-bold">{financialHealth.profitability.net}%</span>
            </div>
            <p className="mt-2 text-sm font-medium">Net Margin</p>
          </div>

          <div className="text-center">
            <div className="relative inline-flex items-center justify-center w-24 h-24">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle cx="48" cy="48" r="36" stroke="currentColor" strokeWidth="8" fill="none" className="text-gray-200"/>
                <circle cx="48" cy="48" r="36" stroke="currentColor" strokeWidth="8" fill="none" 
                  strokeDasharray={`${2 * Math.PI * 36}`}
                  strokeDashoffset={`${2 * Math.PI * 36 * (1 - financialHealth.profitability.operating / 100)}`}
                  className="text-purple-600 transition-all duration-300"/>
              </svg>
              <span className="absolute text-xl font-bold">{financialHealth.profitability.operating}%</span>
            </div>
            <p className="mt-2 text-sm font-medium">Operating Margin</p>
          </div>
        </div>

        {/* Budget vs Actual */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">Budget vs Actual</h4>
          {financialHealth.budgetVsActual.map((item, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">{item.category}</span>
                <span className="text-sm font-medium">
                  {formatCurrency(item.actual)} / {formatCurrency(item.budget)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    item.actual > item.budget ? 'bg-red-500' : 'bg-green-500'
                  }`}
                  style={{width: `${Math.min((item.actual / item.budget) * 100, 100)}%`}}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderInsightsAndRecommendations = () => {
    const insights = [
      {
        type: 'success',
        icon: <ArrowTrendingUpIcon className="w-5 h-5" />,
        title: 'Revenue Growth',
        message: 'Your revenue has increased by 15% this month. Oil change services are performing exceptionally well.',
        action: 'View Details'
      },
      {
        type: 'warning',
        icon: <ExclamationTriangleIcon className="w-5 h-5" />,
        title: 'Inventory Alert',
        message: '5 essential items are running low. Consider restocking brake pads and oil filters.',
        action: 'Check Inventory'
      },
      {
        type: 'info',
        icon: <LightBulbIcon className="w-5 h-5" />,
        title: 'Customer Opportunity',
        message: '23 customers are due for their regular service. Send reminders to increase bookings.',
        action: 'Send Reminders'
      },
      {
        type: 'success',
        icon: <StarIcon className="w-5 h-5" />,
        title: 'High Satisfaction',
        message: 'Customer satisfaction improved to 4.8/5. Keep up the excellent service quality!',
        action: 'View Feedback'
      }
    ];

    const typeColors = {
      success: 'bg-green-50 text-green-800 border-green-200',
      warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
      info: 'bg-blue-50 text-blue-800 border-blue-200',
      danger: 'bg-red-50 text-red-800 border-red-200'
    };

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">AI-Powered Insights</h3>
          <SparklesIcon className="w-5 h-5 text-purple-600" />
        </div>
        <div className="space-y-4">
          {insights.map((insight, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border ${typeColors[insight.type as keyof typeof typeColors]}`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-3">{insight.icon}</div>
                <div className="flex-1">
                  <h4 className="font-medium">{insight.title}</h4>
                  <p className="text-sm mt-1">{insight.message}</p>
                  <button className="text-sm font-medium mt-2 hover:underline">
                    {insight.action} →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader 
        userName={user?.name}
        userRole="garage_owner"
        showNotifications={true}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome back, {user?.name || 'Owner'}
              </h1>
              <p className="text-gray-600 mt-1">
                {user?.garage?.name || 'Al-Salam Auto Service'} - Performance Dashboard
              </p>
            </div>
            <div className="mt-4 lg:mt-0 flex flex-wrap gap-3">
              {/* View Selector */}
              <div className="flex bg-white rounded-lg shadow">
                {['overview', 'financial', 'operations', 'customers'].map((view) => (
                  <button
                    key={view}
                    onClick={() => setSelectedView(view)}
                    className={`px-4 py-2 text-sm font-medium capitalize ${
                      selectedView === view
                        ? 'bg-[#008B8B] text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    } ${
                      view === 'overview' ? 'rounded-l-lg' : 
                      view === 'customers' ? 'rounded-r-lg' : ''
                    }`}
                  >
                    {view}
                  </button>
                ))}
              </div>
              
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm"
              >
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
              </select>

              <button 
                onClick={() => window.location.reload()}
                className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <ArrowPathIcon className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Key Performance Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {renderMetricCard(
            'Today\'s Revenue',
            formatCurrency(stats?.todayRevenue || 0),
            { value: 12.5, isPositive: true },
            <CurrencyDollarIcon className="w-8 h-8 text-[#008B8B]" />,
            'primary'
          )}
          {renderMetricCard(
            'Active Jobs',
            stats?.activeJobs || 0,
            { value: 8.3, isPositive: true },
            <WrenchScrewdriverIcon className="w-8 h-8 text-[#FF8C00]" />,
            'secondary'
          )}
          {renderMetricCard(
            'Customer Satisfaction',
            `⭐ ${stats?.customerSatisfaction || 0}`,
            { value: 2.1, isPositive: true },
            <StarIcon className="w-8 h-8 text-[#10B981]" />,
            'success'
          )}
          {renderMetricCard(
            'Collection Rate',
            `${stats?.collectionRate || 0}%`,
            { value: 1.5, isPositive: true },
            <BanknotesIcon className="w-8 h-8 text-[#8B5CF6]" />,
            'purple'
          )}
        </div>

        {/* Main Content Based on Selected View */}
        {selectedView === 'overview' && (
          <>
            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {renderRevenueOverview()}
              {renderServicePerformance()}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {renderCustomerSegmentation()}
              {renderEmployeePerformanceRadar()}
            </div>

            {/* Insights and Financial Health */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {renderInsightsAndRecommendations()}
              {renderFinancialHealth()}
            </div>
          </>
        )}

        {selectedView === 'financial' && (
          <div className="space-y-6">
            {/* Financial KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Revenue Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Services</span>
                    <span className="font-medium">{formatCurrency(38500)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Parts</span>
                    <span className="font-medium">{formatCurrency(6750)}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t">
                    <span className="font-semibold">Total</span>
                    <span className="font-bold">{formatCurrency(45250)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Expense Summary</h3>
                <div className="space-y-3">
                  {financialHealth?.expenses.map((expense, index) => (
                    <div key={index} className="flex justify-between">
                      <span className="text-gray-600">{expense.category}</span>
                      <span className="font-medium">{formatCurrency(expense.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Profit Analysis</h3>
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">
                    {formatCurrency(14750)}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">Net Profit This Month</p>
                  <div className="mt-4 flex items-center justify-center">
                    <ArrowUpIcon className="w-4 h-4 text-green-500 mr-1" />
                    <span className="text-sm font-medium text-green-600">18.5%</span>
                    <span className="text-sm text-gray-500 ml-1">vs last month</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cash Flow Chart */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Cash Flow Analysis</h3>
              <div className="h-80">
                <Line
                  data={{
                    labels: financialHealth?.cashFlow.map(c => c.month) || [],
                    datasets: [
                      {
                        label: 'Inflow',
                        data: financialHealth?.cashFlow.map(c => c.inflow) || [],
                        borderColor: 'rgb(34, 197, 94)',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        fill: true
                      },
                      {
                        label: 'Outflow',
                        data: financialHealth?.cashFlow.map(c => c.outflow) || [],
                        borderColor: 'rgb(239, 68, 68)',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        fill: true
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        ticks: {
                          callback: (value: any) => formatCurrency(value)
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <button
              onClick={() => navigate('/dashboard/job-cards')}
              className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
            >
              <UsersIcon className="w-8 h-8 text-[#4B0082] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">Customer Check-In</span>
            </button>
            <button
              onClick={() => navigate('/dashboard/job-cards')}
              className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
            >
              <WrenchScrewdriverIcon className="w-8 h-8 text-[#008B8B] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">New Job Card</span>
            </button>
            <button
              onClick={() => navigate('/dashboard/invoices')}
              className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
            >
              <DocumentTextIcon className="w-8 h-8 text-[#FF8C00] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">Create Invoice</span>
            </button>
            <button
              onClick={() => navigate('/dashboard/inventory')}
              className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
            >
              <CubeIcon className="w-8 h-8 text-[#10B981] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">Check Stock</span>
            </button>
            <button
              onClick={() => navigate('/dashboard/reports')}
              className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
            >
              <ChartBarIcon className="w-8 h-8 text-[#8B5CF6] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">View Reports</span>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EnhancedGarageOwnerDashboard;